<template>
  <div
    class="plan-calendar__board"
    :class="{ 'plan-calendar__board--scroll': days.length > 1 }"
    :style="{ '--day-columns': days.length }"
  >
    <div class="plan-calendar__head">
      <div class="plan-calendar__gutter" />
      <div
        v-for="day in days"
        :key="day.iso"
        class="plan-calendar__day"
        :class="{ 'plan-calendar__day--today': day.isToday }"
      >
        <div class="plan-calendar__day-name">{{ day.weekday }}</div>
        <div class="plan-calendar__day-number">{{ day.dayNumber }}</div>
      </div>
    </div>

    <div
      ref="scrollEl"
      class="plan-calendar__scroll"
    >
      <div
        class="plan-calendar__grid"
        :style="{ height: `${layout.height}px` }"
      >
        <div class="plan-calendar__hours">
          <div
            v-for="row in layout.rows"
            :key="row.hour"
            class="plan-calendar__hour"
            :style="{ height: `${row.height}px` }"
          >
            {{ formatHour(row.hour) }}
          </div>
        </div>

        <div
          v-for="day in days"
          :key="`${day.iso}-col`"
          class="plan-calendar__column"
          :class="{ 'plan-calendar__column--today': day.isToday }"
          @mousemove="onColumnMove(day, $event)"
          @mouseleave="clearGhost"
        >
          <div
            v-for="row in layout.rows"
            :key="`${day.iso}-${row.hour}`"
            class="plan-calendar__hour-line"
            :style="{ height: `${row.height}px` }"
          />

          <div
            v-if="showNoActivity"
            class="plan-calendar__empty"
            :style="emptyLabelStyle(day.iso)"
          >
            {{ t('marketing-agent.calendar.no-activity') }}
          </div>

          <button
            v-if="ghost && ghost.date === day.iso"
            type="button"
            class="plan-card plan-card--ghost"
            :style="ghostStyle"
            @click.stop="emit('create-slot', { date: ghost.date, time: ghost.time })"
          >
            <span class="plan-card__head">
              <span class="plan-card__time">{{ slotTime(ghost.time) }}</span>
              <span class="plan-card__ghost-label">{{ t('marketing-agent.outline.add') }}</span>
            </span>
            <v-skeleton-loader
              class="plan-card__image-skeleton"
              type="image"
              boilerplate
            />
            <v-skeleton-loader
              class="plan-card__text-skeleton"
              type="text@3"
              boilerplate
            />
          </button>

          <button
            v-for="slot in slotsByDate[day.iso] || []"
            :key="slot.id"
            type="button"
            class="plan-card"
            :class="`plan-card--${eventVariant(slot)}`"
            :style="cardStyle(slot)"
            @click="emit('slot-click', slot)"
          >
            <span class="plan-card__head">
              <span class="plan-card__time">{{ slotTime(slot.scheduledLocal.time) }}</span>
              <span
                v-if="cardTitle(slot)"
                class="plan-card__topic"
              >
                {{ cardTitle(slot) }}
              </span>
              <v-icon
                class="plan-card__platform"
                :class="`plan-card__platform--${(slot.platform || '').toLowerCase()}`"
                :icon="platformIcon(slot)"
                size="16"
              />
            </span>

            <img
              v-if="slot.imageUrl"
              class="plan-card__image"
              :src="slot.imageUrl"
              alt=""
            />
            <v-skeleton-loader
              v-else
              class="plan-card__image-skeleton"
              type="image"
              :boilerplate="!isImageLoading(slot)"
            />

            <p
              v-if="cardBody(slot, t)"
              class="plan-card__text"
            >
              {{ cardBody(slot, t) }}
            </p>
            <v-skeleton-loader
              v-else
              class="plan-card__text-skeleton"
              type="text@3"
              :boilerplate="!isCardLoading(slot)"
            />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { formatIso } from './calendarDates'
import {
  cardBody,
  cardTitle,
  eventVariant,
  groupSlotsByDate,
  isCardLoading,
  isImageLoading,
  platformIcon,
  slotTime,
} from './slotCard'
import { buildWeekLayout, CARD_PX, earliestVisibleTime, hourRangeFor } from './weekLayout'

const props = defineProps({
  days: {
    type: Array,
    default: () => [],
  },
  slots: {
    type: Array,
    default: () => [],
  },
  creatable: {
    type: Boolean,
    default: false,
  },
  showNoActivity: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['slot-click', 'create-slot'])

const { t, locale } = useI18n()
const ghost = ref(null)
const scrollEl = ref(null)

const slotsByDate = computed(() => groupSlotsByDate(props.slots))
const hourRange = computed(() =>
  hourRangeFor(
    props.days.map((day) => day.iso),
    props.slots
  )
)
const layout = computed(() =>
  buildWeekLayout(
    props.days.map((day) => day.iso),
    props.slots,
    hourRange.value
  )
)

watch(
  () => props.days.map((day) => day.iso).join(),
  () => {
    ghost.value = null
  }
)

const firstVisibleTime = computed(() =>
  earliestVisibleTime(
    props.days.map((day) => day.iso),
    props.slots
  )
)

// Bring the first post of the shown days into view on mount and whenever the
// days change (view switch or arrows), and once when posts arrive for days
// already shown. Edits to posts on the same days keep the user's scroll position.
function scrollToFirstPost() {
  if (!scrollEl.value) return
  const time = firstVisibleTime.value
  scrollEl.value.scrollTop = time ? Math.max(0, layout.value.timeTop(time) - 24) : 0
}

onMounted(scrollToFirstPost)
watch(
  () => `${props.days.map((day) => day.iso).join()}|${firstVisibleTime.value != null}`,
  scrollToFirstPost,
  { flush: 'post' }
)

function cardStyle(slot) {
  return {
    top: `${layout.value.slotTop(slot)}px`,
    height: `${CARD_PX}px`,
    left: '2px',
    right: '2px',
  }
}

function formatHour(hour) {
  return new Intl.DateTimeFormat(locale.value, { hour: 'numeric' }).format(
    new Date(2000, 0, 1, hour, 0)
  )
}

function emptyLabelStyle(iso) {
  const bottoms = (slotsByDate.value[iso] || []).map((slot) => layout.value.slotTop(slot) + CARD_PX)
  if (ghost.value?.date === iso) {
    bottoms.push(layout.value.timeTop(ghost.value.time) + CARD_PX)
  }
  if (!bottoms.length) return {}
  return { top: `${Math.max(...bottoms) + 16}px` }
}

const ghostStyle = computed(() => {
  if (!ghost.value) return {}
  return {
    top: `${layout.value.timeTop(ghost.value.time)}px`,
    height: `${CARD_PX}px`,
  }
})

function onColumnMove(day, event) {
  if (!props.creatable || day.iso <= formatIso(new Date())) {
    ghost.value = null
    return
  }
  if (event.target.closest('.plan-card:not(.plan-card--ghost)')) {
    ghost.value = null
    return
  }
  const bounds = event.currentTarget.getBoundingClientRect()
  const offsetY = event.clientY - bounds.top
  const hour = layout.value.hourAt(offsetY)
  ghost.value = { date: day.iso, time: `${String(hour).padStart(2, '0')}:00` }
}

function clearGhost() {
  ghost.value = null
}
</script>

<style scoped>
.plan-calendar__board {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  border-top: 1px solid #ececec;
  border-bottom: 1px solid #ececec;
  background: #fff;
}

.plan-calendar__head,
.plan-calendar__grid {
  display: grid;
  grid-template-columns: 56px repeat(var(--day-columns, 7), minmax(0, 1fr));
}

.plan-calendar__head {
  flex-shrink: 0;
  border-bottom: 1px solid #ececec;
}

.plan-calendar__scroll {
  flex: 1;
  min-height: 0;
  overflow: auto;
}

.plan-calendar__grid {
  position: relative;
  min-height: 100%;
  padding-bottom: 200px;
}

.plan-calendar__hour {
  box-sizing: border-box;
  padding: 2px 8px 0 0;
  border-bottom: 1px solid #f2f2f2;
  color: #8a8a8a;
  font-size: 11px;
  line-height: 1;
  text-align: right;
}

.plan-calendar__column {
  position: relative;
  min-width: 0;
  border-left: 1px solid #ececec;
}

.plan-calendar__hour-line {
  box-sizing: border-box;
  border-bottom: 1px solid #f2f2f2;
}

.plan-calendar__column--today,
.plan-calendar__day--today {
  background: rgba(var(--v-theme-primary), 0.05);
}

.plan-calendar__day {
  padding: 6px 4px 8px;
  text-align: center;
}

.plan-calendar__day-name {
  font-size: 13px;
  font-weight: 700;
  color: #222;
}

.plan-calendar__day-number {
  font-size: 12px;
  color: #8a8a8a;
}

.plan-calendar__empty {
  position: absolute;
  top: 42%;
  left: 0;
  right: 0;
  z-index: 0;
  color: #b0b0b0;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-align: center;
  text-transform: uppercase;
  pointer-events: none;
}

.plan-card {
  position: absolute;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 10px 10px;
  overflow: hidden;
  border: 1px solid rgb(var(--v-theme-primary));
  border-radius: 10px;
  background: #fff;
  color: rgb(var(--v-theme-primary));
  text-align: left;
  cursor: pointer;
}

.plan-card:hover {
  z-index: 2;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08);
}

.plan-card--failed {
  border-color: #c62828;
  color: #c62828;
}

.plan-card--ghost {
  left: 2px;
  z-index: 2;
  width: calc(100% - 4px);
  border-style: dashed;
  background: rgba(var(--v-theme-primary), 0.06);
}

.plan-card__head {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.plan-card__time {
  flex: 0 0 auto;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.2;
}

.plan-card__topic {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.plan-card__ghost-label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  font-size: 12px;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.plan-card__platform {
  flex: 0 0 auto;
  margin-left: auto;
}

.plan-card__platform--instagram {
  background: radial-gradient(circle at 30% 110%, #fccc63, #f77737 35%, #e1306c 60%, #833ab4);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.plan-card__image,
.plan-card__image-skeleton {
  flex: 1 1 auto;
  width: 100%;
  min-height: 96px;
  border-radius: 8px;
  overflow: hidden;
}

.plan-card__image {
  object-fit: cover;
  background: #eee;
}

.plan-card__image-skeleton {
  display: flex;
}

.plan-card__image-skeleton :deep(.v-skeleton-loader__image) {
  flex: 1;
  height: 100%;
  margin: 0;
  border-radius: 8px;
}

.plan-card__text {
  display: -webkit-box;
  margin: 0;
  overflow: hidden;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.35;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
}

.plan-card__text-skeleton {
  flex: 0 0 auto;
}

.plan-card__text-skeleton :deep(.v-skeleton-loader__text) {
  margin: 0 0 6px;
  border-radius: 6px;
}

.plan-card__text-skeleton :deep(.v-skeleton-loader__text:nth-child(1)) {
  max-width: 100%;
}

.plan-card__text-skeleton :deep(.v-skeleton-loader__text:nth-child(2)) {
  max-width: 78%;
}

.plan-card__text-skeleton :deep(.v-skeleton-loader__text:nth-child(3)) {
  max-width: 52%;
  margin-bottom: 0;
}

@media (max-width: 1100px) {
  .plan-calendar__board--scroll .plan-calendar__head,
  .plan-calendar__board--scroll .plan-calendar__grid {
    min-width: 780px;
  }
}
</style>
